import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  defaultFollowConfig,
  accountStatusConfig,
  type BrokerAccount,
  type FollowRecord,
  type FollowConfig,
} from '@/lib/brokerAccounts';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/services/appApi';
import { forexApi, toApiAgentId, toBrokerAccount, toFollowRecord, type PlatformAgentDTO, type ReviewedStrategyDTO, type SubscriptionAuthorizationDTO, type SubscriptionConsentPut } from '@/services/forexApi';

import FollowActionDialog from '@/components/feature/FollowActionDialog';
import { authorizationLabel, readinessLabel, sameReviewedStrategy } from '@/lib/followPresentation';

type AgentView = {
  id: number | string;
  apiId: string;
  name: string;
  strategy_type: string;
  strategy_label: string;
  risk_level: string;
  risk_label: string;
  color: string;
  supported_pairs: string[];
  total_return: number | null;
  win_rate: number | null;
  max_drawdown: number | null;
  follower_count: number | null;
};

const AGENT_COLORS = ['#10B981', '#F59E0B', '#6366F1', '#EF4444'];

function strategyLabel(strategyType: string): string {
  const labels: Record<string, string> = {
    trend_following: '趋势跟踪',
    breakout: '突破策略',
    grid: '网格交易',
  };
  return labels[strategyType] || strategyType || '外汇策略';
}

function riskLabel(riskLevel: string): string {
  const labels: Record<string, string> = {
    strict: '稳健',
    standard: '标准',
    aggressive: '进取',
    conservative: '保守',
  };
  return labels[riskLevel] || riskLevel || '标准';
}

function agentColorIndex(agentId: number | string): number {
  if (typeof agentId === 'number') return Math.abs(agentId - 1) % AGENT_COLORS.length;
  return [...agentId].reduce((sum, char) => sum + char.charCodeAt(0), 0) % AGENT_COLORS.length;
}

function toAgentView(agent: PlatformAgentDTO | null, fallbackId: number | string): AgentView {
  if (!agent) {
    return {
      id: fallbackId,
      apiId: toApiAgentId(fallbackId),
      name: '外汇智能体',
      strategy_type: 'trend_following',
      strategy_label: '趋势跟踪',
      risk_level: 'standard',
      risk_label: '标准',
      color: AGENT_COLORS[agentColorIndex(fallbackId)],
      supported_pairs: ['EURUSD'],
      total_return: 0,
      win_rate: 0,
      max_drawdown: 0,
      follower_count: 0,
    };
  }
  return {
    id: fallbackId,
    apiId: agent.agent_id,
    name: agent.name,
    strategy_type: agent.strategy_type,
    strategy_label: strategyLabel(agent.strategy_type),
    risk_level: agent.risk_level,
    risk_label: riskLabel(agent.risk_level),
    color: AGENT_COLORS[agentColorIndex(fallbackId)],
    supported_pairs: agent.supported_symbols,
    total_return: agent.total_return,
    win_rate: agent.win_rate,
    max_drawdown: agent.max_drawdown,
    follower_count: agent.follower_count,
  };
}

const riskLevelStyles: Record<string, { color: string; bg: string }> = {
  conservative: { color: '#3B82F6', bg: '#EFF6FF' },
  moderate: { color: '#F59E0B', bg: '#FFFBEB' },
  aggressive: { color: '#EF4444', bg: '#FEF2F2' },
};

// ═══════════════════════════════════════
//  CopyConfigPage
// ═══════════════════════════════════════
export default function CopyConfigPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [searchParams] = useSearchParams();
  const agentIdParam = searchParams.get('agentId') || '1';
  const validation = searchParams.get('validation') === '1';
  const agentId: number | string = /^\d+$/.test(agentIdParam) ? Number(agentIdParam) : agentIdParam;
  const [apiAgent, setApiAgent] = useState<PlatformAgentDTO | null>(null);
  const [reviewedStrategy, setReviewedStrategy] = useState<ReviewedStrategyDTO | null>(null);
  const agent = useMemo(() => toAgentView(apiAgent, agentId), [apiAgent, agentId]);

  // ── 账户相关 ──
  const [allAccounts, setAllAccounts] = useState<BrokerAccount[]>([]);
  const [showAccountSheet, setShowAccountSheet] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [sheetDraftIds, setSheetDraftIds] = useState<string[]>([]);

  // ── 跟单配置 ──
  const [config, setConfig] = useState<FollowConfig>({
    ...defaultFollowConfig,
    allowedPairs: [...agent.supported_pairs],
  });

  // ── 风险确认 ──
  const [riskConfirmed, setRiskConfirmed] = useState(false);

  // ── 提交状态 ──
  const [submitting, setSubmitting] = useState(false);
  const mutationLock = useRef(false);
  const [confirmation, setConfirmation] = useState<'save' | 'pause' | 'stop' | string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── 已有跟单记录 ──
  const [existingFollows, setExistingFollows] = useState<FollowRecord[]>([]);
  const [allFollows, setAllFollows] = useState<FollowRecord[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const policyRequestKeys=useRef<Record<string,string>>({});
  const [subscriptions, setSubscriptions] = useState<Record<string, SubscriptionAuthorizationDTO>>({});

  const readSequence = useRef(0);
  const refreshData = useCallback(async () => {
    const request = ++readSequence.current;
    if (!accessToken) {
      setLoadingData(false);
      setErrorMsg('请先登录后再配置跟单');
      return false;
    }
    setLoadingData(true);
    setRiskConfirmed(false);
    try {
      const agentData = await (validation
        ? forexApi.getValidationAgent(agentId, accessToken)
        : forexApi.getPlatformAgent(agentId, accessToken)).catch((error) => {
        if (error instanceof ApiError && error.status === 404)
          return forexApi.getValidationAgent(agentId, accessToken);
        throw error;
      });
      const [accountData, followData, reviewed] = await Promise.all([
        forexApi.listFollowerAccounts(accessToken, undefined, toApiAgentId(agentId), validation),
        forexApi.listFollowRelations(accessToken),
        forexApi.getReviewedStrategy(toApiAgentId(agentId), accessToken,
          agentData.validation_binding_id).catch(error => {
          if (error instanceof ApiError && error.status === 404 && error.code === 'REVIEWED_STRATEGY_NOT_FOUND') return null;
          throw error;
        }),
      ]);
      const accounts = accountData.map(toBrokerAccount);
      const follows = followData.map((relation) => toFollowRecord(relation, accounts));
      const activeRecords = follows.filter((r) =>
        r.agentId === agentId && followData.some(d => d.id === r.id && d.execution_schema === 'account_parallel') && ['running', 'paused', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'configuring', 'pending_risk_confirm'].includes(r.followStatus)
      );

      const authorizations=await Promise.all(activeRecords.map(r=>forexApi.getSubscriptionAuthorization(r.id,accessToken)));
      if (request !== readSequence.current) return false;
      setApiAgent(agentData);
      setReviewedStrategy(reviewed);
      setErrorMsg(null);
      setAllAccounts(accounts);
      setAllFollows(follows);
      setExistingFollows(activeRecords);
      setSubscriptions(Object.fromEntries(authorizations.map(p=>[p.relation_id,p])));

      if (activeRecords.length > 0) {
        setSelectedAccountIds(activeRecords.map((r) => r.accountId).filter((id) =>
          !agentData.is_validation || agentData.eligible_follower_account_ids?.includes(id)));
        setConfig({ ...defaultFollowConfig, ...activeRecords[0].config } as FollowConfig);
      } else {
        setSelectedAccountIds([]);
        setConfig((prev) => ({
          ...prev,
          allowedPairs: agentData.supported_symbols.length > 0 ? [...agentData.supported_symbols] : prev.allowedPairs,
        }));
      }
      return true;
    } catch {
      if (request !== readSequence.current) return false;
      setErrorMsg('跟随数据暂不可用，请刷新后再操作。');
      return false;
    } finally {
      if (request === readSequence.current) setLoadingData(false);
    }
  }, [accessToken, agentId, validation]);

  useEffect(() => {
    void refreshData();
    return () => { readSequence.current += 1; };
  }, [refreshData]);

  // ── 选中账户对象列表 ──
  const selectedAccounts = useMemo(
    () => allAccounts.filter((a) => selectedAccountIds.includes(a.id)
      && (!apiAgent?.is_validation || apiAgent.eligible_follower_account_ids?.includes(a.id))),
    [allAccounts, selectedAccountIds, apiAgent],
  );

  // ── 是否为编辑模式 ──
  const isEditMode = existingFollows.length > 0;

  // ── 分类账户列表 ──
  const accountGroups = useMemo(() => {
    const connected: BrokerAccount[] = [];
    const followingOthers: BrokerAccount[] = [];
    const reviewing: BrokerAccount[] = [];
    const released: BrokerAccount[] = [];

    allAccounts.forEach((acc) => {
      if (apiAgent?.is_validation && !apiAgent.eligible_follower_account_ids?.includes(acc.id)) return;
      if (acc.accountStatus === 'connected') {
        const activeFollow = allFollows.find((follow) =>
          follow.accountId === acc.id && ['running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'configuring', 'pending_risk_confirm', 'status_unknown'].includes(follow.followStatus)
        );
        if (activeFollow && activeFollow.agentId !== agentId) {
          followingOthers.push(acc);
        } else {
          connected.push(acc);
        }
      } else if (acc.accountStatus === 'reviewing') {
        reviewing.push(acc);
      } else if (acc.accountStatus === 'released') {
        released.push(acc);
      }
    });
    return { connected, followingOthers, reviewing, released };
  }, [allAccounts, allFollows, agentId, apiAgent]);

  const isSelectableAccount = useCallback((acc: BrokerAccount) =>
    acc.accountStatus === 'connected' && acc.usageStatus !== 'occupied'
    && acc.symbolCompatibility?.eligible === true
    && (!apiAgent?.is_validation || apiAgent.eligible_follower_account_ids?.includes(acc.id) === true)
    && !allFollows.some((follow) => follow.accountId === acc.id && follow.agentId !== agentId
      && ['running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'configuring', 'pending_risk_confirm', 'status_unknown'].includes(follow.followStatus)),
  [agentId, allFollows, apiAgent]);
  const selectableAccounts = useMemo(() => accountGroups.connected.filter(isSelectableAccount), [accountGroups, isSelectableAccount]);

  // ── 按钮启用条件 ──
  const canSubmit = useMemo(() => {
    if (loadingData || submitting || errorMsg || selectedAccountIds.length === 0) return false;
    if (!riskConfirmed) return false;
    if (!reviewedStrategy) return false;
    if (config.allowedPairs.length === 0) return false;
    return selectedAccounts.length === selectedAccountIds.length && selectedAccounts.every(isSelectableAccount);
  }, [loadingData, submitting, errorMsg, selectedAccountIds, selectedAccounts, riskConfirmed, reviewedStrategy, config.allowedPairs, isSelectableAccount]);

  // ── 打开弹层 ──
  const openAccountSheet = () => {
    setSheetDraftIds([...selectedAccountIds]);
    setShowAccountSheet(true);
  };

  // ── 弹层内切换 ──
  const toggleDraftAccount = (accountId: string) => {
    const acc = allAccounts.find((a) => a.id === accountId);
    if (!acc) return;
    if (sheetDraftIds.includes(accountId)) {
      setSheetDraftIds((prev) => prev.filter((id) => id !== accountId));
      return;
    }
    if (acc.symbolCompatibility?.eligible !== true) { setErrorMsg(acc.symbolCompatibility?.message || '品种待验证，暂不可选，请刷新后重试'); return; }
    if(acc.usageStatus==='occupied') { setErrorMsg('该账户仍有持仓待处理，完成退出后可重新跟单');return; }

    if (acc.accountStatus === 'connected') {
      const activeFollow = allFollows.find((follow) =>
        follow.accountId === accountId && ['running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'configuring', 'pending_risk_confirm', 'status_unknown'].includes(follow.followStatus)
      );
      if (activeFollow && activeFollow.agentId !== agentId) {
        setErrorMsg('该账户正在跟随其他智能体，一个账户同时只能跟随一个智能体');
        setTimeout(() => setErrorMsg(null), 3000);
        return;
      }
    } else {
      setErrorMsg(accountStatusConfig[acc.accountStatus]?.description || '该账户当前不可用于跟单');
      setTimeout(() => setErrorMsg(null), 3000);
      return;
    }

    if (!isSelectableAccount(acc)) { setErrorMsg('该账户当前不符合跟随资格，请刷新状态后重试'); return; }
    setSheetDraftIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  // ── 弹层确认 ──
  const confirmSheetSelection = () => {
    setRiskConfirmed(false);
    setSelectedAccountIds(sheetDraftIds);
    setShowAccountSheet(false);
  };

  // ── 移除单个已选账户 ──
  const removeSelectedAccount = (accountId: string) => {
    setRiskConfirmed(false);
    setSelectedAccountIds((prev) => prev.filter((id) => id !== accountId));
  };

  // ── 处理提交 ──
  const handleSubmit = async () => {
    if (!canSubmit || !accessToken || mutationLock.current) return;
    mutationLock.current = true;
    setSubmitting(true);
    try {
      const latestReviewed = await forexApi.getReviewedStrategy(toApiAgentId(agentId), accessToken, apiAgent?.validation_binding_id);
      if (!sameReviewedStrategy(reviewedStrategy, latestReviewed)) throw new Error('strategy changed');
      let pendingReadiness=false;
      for (const acc of selectedAccounts) {
        const existingRecord = existingFollows.find((r) => r.accountId === acc.id);
        const relation = existingRecord || await forexApi.createFollowRelation({
            id: '',
            agentId: agent.id,
            accountId: acc.id,
            accountNo: acc.accountNo,
            server: acc.server,
            brokerName: acc.brokerName,
            followStatus: 'configuring',
            config: config as unknown as Record<string, unknown>,
            createdAt: '',
            updatedAt: '',
          }, accessToken, policyRequestKeys.current[`create:${agent.apiId}:${acc.id}`] ||= crypto.randomUUID(),
          apiAgent?.validation_binding_id);
        let current = await forexApi.getSubscriptionAuthorization(relation.id,accessToken);
        const reviewed=apiAgent?.is_validation ? reviewedStrategy : current.reviewed_strategy;
        if (!reviewed || !sameReviewedStrategy(reviewedStrategy, reviewed)) throw new Error('策略已变化，请重新读取并确认当前版本');
        const needsConsent = current.state !== 'active'
          || current.validation_binding_id !== apiAgent?.validation_binding_id
          || current.consented_risk_hash !== reviewed.risk_hash
          || current.consented_strategy_ref?.id !== reviewed.strategy_ref.id
          || current.consented_strategy_ref?.version !== reviewed.strategy_ref.version
          || current.consented_strategy_ref?.content_hash !== reviewed.strategy_ref.content_hash;
        if (needsConsent) {
          const consent: SubscriptionConsentPut = {
            expected_revision: current.revision ?? 0,
            consented_strategy_ref: reviewed.strategy_ref,
            consented_risk_hash: reviewed.risk_hash,
            state: 'active',
            ...(apiAgent?.validation_binding_id
              ? { validation_binding_id: apiAgent.validation_binding_id } : {}),
          };
          const requestScope=relation.id+JSON.stringify(consent);
          const requestKey=policyRequestKeys.current[requestScope] ||= crypto.randomUUID();
          await forexApi.saveSubscriptionAuthorization(relation.id,consent,requestKey,accessToken);
        }
        if (existingRecord?.followStatus !== 'running') await forexApi.resumeFollowRelation(relation.id,accessToken);
        current=await forexApi.getSubscriptionAuthorization(relation.id,accessToken);
        pendingReadiness ||= !current.effective_for_new_opens;
      }

      for (const record of existingFollows) {
        if (!selectedAccountIds.includes(record.accountId)) {
          await forexApi.stopFollowRelation(record.id, accessToken);
        }
      }

      const refreshed = await refreshData();
      if (!refreshed) throw new Error('readback');
      setToast({ msg: pendingReadiness ? '订阅已保存，执行端待就绪；待就绪期间不会开新仓' : `${selectedAccounts.length} 个账户已订阅策略`, type: 'success' });
      setConfirmation(null);
    } catch (error) {
      setConfirmation(null);
      await refreshData();
      setErrorMsg(error instanceof ApiError && error.status === 409 ? '策略或授权已变化，请刷新并重新确认。' : '提交未完整完成。请先刷新并核对每个账户状态，已成功的账户无需重复授权。');
      setRiskConfirmed(false);
    } finally {
      mutationLock.current = false;
      setSubmitting(false);
    }
  };

  const handleConfirmedAction = async () => {
    if (confirmation === 'save') { await handleSubmit(); return; }
    if (!confirmation || !accessToken || mutationLock.current) return;
    mutationLock.current = true;
    setSubmitting(true);
    try {
      if (confirmation === 'pause' || confirmation === 'stop') {
        const targets = existingFollows.filter(r => confirmation === 'pause' ? r.followStatus === 'running' : r.followStatus !== 'stopped');
        const results = await Promise.allSettled(targets.map(r => confirmation === 'pause'
          ? forexApi.pauseFollowRelation(r.id, accessToken) : forexApi.stopFollowRelation(r.id, accessToken)));
        if (results.some(result => result.status === 'rejected')) throw new Error('partial');
      } else {
        const relationId = confirmation;
        const displayed = subscriptions[relationId];
        const current = await forexApi.getSubscriptionAuthorization(relationId, accessToken);
        if (!current.reviewed_strategy || !sameReviewedStrategy(displayed?.reviewed_strategy || null, current.reviewed_strategy)) throw new Error('changed');
        const payload: SubscriptionConsentPut = {
          expected_revision: current.revision ?? 0,
          consented_strategy_ref: current.reviewed_strategy.strategy_ref,
          consented_risk_hash: current.reviewed_strategy.risk_hash,
          state: 'revoked',
          ...(current.validation_binding_id ? { validation_binding_id: current.validation_binding_id } : {}),
        };
        const key = relationId + JSON.stringify(payload);
        await forexApi.saveSubscriptionAuthorization(relationId, payload,
          policyRequestKeys.current[key] ||= crypto.randomUUID(), accessToken);
      }
      const refreshed = await refreshData();
      if (!refreshed) throw new Error('readback');
      setToast({ msg: '操作已提交，最新关系与授权状态已重新读取。此操作不代表平仓。', type: 'success' });
    } catch {
      await refreshData();
      setErrorMsg('操作未完整完成。请刷新核对各账户状态后再继续，不要重复提交未知结果。');
    } finally {
      mutationLock.current = false;
      setSubmitting(false);
      setConfirmation(null);
    }
  };

  const hasRunning = existingFollows.some((r) => r.followStatus === 'running');
  const hasActive = existingFollows.some((r) => r.followStatus !== 'stopped');

  return (
    <div className="min-h-screen bg-gray-50 pb-36 overflow-x-hidden">
      {/* ── Header ── */}
      <div className="px-5 pb-5" style={{ background: `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)`, paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/80 text-sm mb-4">
          <i className="ri-arrow-left-s-line text-xl" />
          返回
        </button>
        <h1 className="text-white font-bold text-lg">策略授权与跟随管理</h1>
        <p className="text-white/70 text-xs mt-1">
          {isEditMode ? `管理当前 ${existingFollows.length} 个账户的跟单规则` : '可同时选择多个账户一起跟随'}
        </p>
      </div>

      <fieldset disabled={submitting} className="min-w-0 px-5 mt-4 flex flex-col gap-4">

        {/* ═══ 模块一：跟随智能体 ═══ */}
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-3">跟随智能体</p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: agent.color }}>
              <i className="ri-robot-2-fill text-white text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-bold text-sm">{agent.name}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium"
                  style={{ background: `${agent.color}18`, color: agent.color }}>
                  {agent.strategy_label}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium"
                  style={{ background: riskLevelStyles[agent.risk_level]?.bg, color: riskLevelStyles[agent.risk_level]?.color }}>
                  {agent.risk_label}
                </span>
              </div>
              <p className="text-gray-400 text-[10px] mt-1 truncate">
                支持 {agent.supported_pairs.join(' · ')}
              </p>
            </div>
          </div>
        </div>

        {/* ═══ 模块二：选择交易账户（多选）═══ */}
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-[10px] uppercase tracking-wider">交易账户</p>
            {selectedAccountIds.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ background: `${agent.color}18`, color: agent.color }}>
                已选 {selectedAccountIds.length} 个
              </span>
            )}
          </div>

          {loadingData ? (
            <div className="flex items-center gap-3 py-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 flex-shrink-0">
                <i className="ri-loader-4-line text-gray-400 text-lg animate-spin" />
              </div>
              <div className="flex-1">
                <p className="text-gray-900 font-semibold text-sm">正在同步交易账户</p>
                <p className="text-gray-400 text-xs mt-0.5">从服务端读取已审核账户和跟单状态</p>
              </div>
            </div>
          ) : allAccounts.length === 0 ? (
            <div className="flex items-center gap-3 py-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                <i className="ri-error-warning-line text-amber-500 text-lg" />
              </div>
              <div className="flex-1">
                <p className="text-gray-900 font-semibold text-sm">请先绑定交易账户</p>
                <p className="text-gray-400 text-xs mt-0.5">绑定交易账户后才能开启智能跟单</p>
              </div>
            </div>
          ) : selectedAccounts.length > 0 ? (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedAccounts.map((acc) => {
                const existRecord = existingFollows.find((r) => r.accountId === acc.id);
                return (
                  <div key={acc.id}
                    className="flex items-center gap-1.5 rounded-full pl-2 pr-1 py-1"
                    style={{ background: `${agent.color}10`, border: `1px solid ${agent.color}30` }}>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: agent.color, color: 'white' }}>
                      {acc.type.toUpperCase()}
                    </span>
                    <span className="text-xs font-semibold text-gray-700" style={{ letterSpacing: '0.02em' }}>
                      {acc.accountNo.length > 6 ? `····${acc.accountNo.slice(-4)}` : acc.accountNo}
                    </span>
                    {existRecord && (
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                    )}
                    <button
                      onClick={() => removeSelectedAccount(acc.id)}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-white/70 flex-shrink-0 active:scale-90 transition-transform"
                    >
                      <i className="ri-close-line text-gray-400" style={{ fontSize: '10px' }} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-sm mb-3">可同时选择多个账户批量开启跟单</p>
          )}

          <button
            onClick={openAccountSheet}
            className="w-full py-2.5 rounded-xl border border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors active:scale-[0.98]"
            style={{ borderColor: `${agent.color}50`, color: agent.color, background: `${agent.color}04` }}
          >
            <i className="ri-add-line" />
            {selectedAccountIds.length > 0 ? '管理已选账户' : '选择交易账户（可多选）'}
          </button>

          <p className="text-gray-400 text-[10px] mt-2">
            同一账户同一时间只能订阅一个智能体 · 多账户各自独立执行
          </p>
        </div>

        {/* 用户只授权当前显示的策略版本。 */}
        {selectedAccountIds.length > 0 && (
          <div className="bg-white rounded-2xl p-4 space-y-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <p className="text-gray-800 text-sm font-semibold">当前策略与执行风险</p>
              {reviewedStrategy && <p className="text-xs text-gray-600">版本 {reviewedStrategy.strategy_ref.version}</p>}
            </div>
            {reviewedStrategy ? (
              <dl className="space-y-2 text-xs text-gray-600">
                {[
                  ['固定手数', `${reviewedStrategy.risk_summary.fixed_lot} lot / 账号`],
                  ['单笔上限', `${reviewedStrategy.risk_summary.max_lot_per_order} lot`],
                  ['总手数上限', `${reviewedStrategy.risk_summary.max_total_lots} lot`],
                  ['最大同时持仓', reviewedStrategy.risk_summary.max_open_positions],
                  ['最大日亏损', `${reviewedStrategy.risk_summary.max_daily_loss} ${reviewedStrategy.risk_summary.currency}`],
                  ['最低可用保证金', `${reviewedStrategy.risk_summary.min_free_margin} ${reviewedStrategy.risk_summary.currency}`],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-start justify-between gap-4">
                    <dt className="shrink-0">{label}</dt>
                    <dd className="min-w-0 break-words text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            ) : <p className="text-xs text-amber-700">当前策略尚未完成审核，无法授权。</p>}
            {existingFollows.map(record => {
              const state = subscriptions[record.id];
              const effectiveRisk = state?.account_effective_risk;
              return <div key={record.id} className="space-y-2 text-xs text-gray-600">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1 font-semibold">
                  <p>账户尾号 {record.accountNo.slice(-4)}</p>
                  <p className={state?.readiness === 'needs_reauthorization' && state.state !== 'revoked' && state.state !== 'paused' ? 'text-amber-700' : ''}>{authorizationLabel(state)}</p>
                </div>
                <p>{readinessLabel(state)}</p>
                {effectiveRisk?.status === 'available' && effectiveRisk.limits ? <div className="space-y-1 break-words">
                  <p className="font-semibold">账户实际限制</p>
                  <p>日亏上限：{effectiveRisk.limits.max_daily_loss} {effectiveRisk.currency}；总手数上限：{effectiveRisk.limits.max_total_lots} lot</p>
                  <p>最大同时持仓：{effectiveRisk.limits.max_open_positions}；最低可用保证金：{effectiveRisk.limits.min_free_margin} {effectiveRisk.currency}</p>
                  {effectiveRisk.sources?.map((source, index) => <p key={index} className={source.agent_lifecycle === 'deleting' ? 'text-amber-700' : ''}>
                    风控来源：{source.agent_name || '历史策略'} v{source.strategy_version}，日亏上限 {source.max_daily_loss} {effectiveRisk.currency}{source.agent_lifecycle === 'deleting' ? '（删除中，未决交易义务结清前保留）' : ''}
                  </p>)}
                </div> : <p className="text-amber-700">账户实际风险上限暂不可用，请刷新或联系平台核对。</p>}
                {state && state.state !== 'revoked' && state.reviewed_strategy && <button type="button" disabled={submitting || loadingData} onClick={() => setConfirmation(record.id)} className="min-h-11 text-left text-indigo-600 disabled:opacity-50">撤销此账户策略授权</button>}
              </div>;
            })}
            {reviewedStrategy && <p className="text-xs text-amber-700">这些参数来自平台{apiAgent?.is_validation ? '审核前验证' : '已审核'}策略，跟随账户不可单独修改。{apiAgent?.is_validation ? '仅当前测试账户可授权；正式上线后需重新确认正式版本。' : '授权持续有效，可随时暂停或撤销；策略或风险变化时需重新确认。'}</p>}
            <div className="flex flex-wrap gap-2">
              {agent.supported_pairs.map(pair => {
                const selected = config.allowedPairs.includes(pair);
                return <button key={pair} onClick={() => { setRiskConfirmed(false); setConfig(c => ({ ...c, allowedPairs: selected ? c.allowedPairs.filter(p => p !== pair) : [...c.allowedPairs, pair] })); }}
                  className="px-3 py-1.5 rounded-full text-xs" style={{ background: selected ? `${agent.color}18` : '#F3F4F6', color: selected ? agent.color : '#6B7280' }}>{pair}</button>;
              })}
            </div>
          </div>
        )}

        {/* ═══ 模块四：确认风险 ═══ */}
        {selectedAccountIds.length > 0 && (
          <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={riskConfirmed}
                disabled={loadingData || submitting} onChange={(e) => setRiskConfirmed(e.target.checked)} className="mt-1 h-5 w-5 accent-indigo-600" />
              <p className="text-gray-600 text-xs leading-relaxed">
                我已了解外汇交易风险，并同意所选 {selectedAccountIds.length} 个账户订阅当前{apiAgent?.is_validation ? '审核前验证' : '已审核'}策略版本。各账户将独立评估风险并执行；{apiAgent?.is_validation ? '正式上线后需另行授权正式版本。' : '授权持续有效，可随时暂停或撤销；策略或风险变化时需重新确认。'}
              </p>
            </label>
            <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
              <i className="ri-error-warning-line text-amber-400 text-sm flex-shrink-0 mt-0.5" />
              <div className="text-amber-700 text-[11px] leading-relaxed space-y-0.5">
                <p>跟单不保证收益，外汇交易具有高风险。</p>
                <p>请确认账户、{apiAgent?.is_validation ? '验证中' : '已审核'}策略版本和只读风险参数后再授权。</p>
                <p>同一交易账户同一时间只能跟随一个 AI 智能体。</p>
              </div>
            </div>
          </div>
        )}
      </fieldset>

      {errorMsg && <div role="alert" className="mx-5 mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
        <p>{errorMsg}</p><button type="button" disabled={submitting || loadingData} onClick={() => void refreshData()} className="mt-2 min-h-11 rounded-xl border px-4">刷新并核对状态</button>
      </div>}
      {confirmation && <FollowActionDialog busy={submitting}
        title={confirmation === 'save' ? '确认保存策略授权' : confirmation === 'pause' ? '暂停全部跟随？' : confirmation === 'stop' ? '停止全部跟随？' : '撤销此账户策略授权？'}
        description={confirmation === 'save'
          ? `所选账户：${selectedAccounts.map(a => '尾号 ' + a.accountNo.slice(-4)).join('、')}。策略版本：${reviewedStrategy?.strategy_ref.version ?? '—'}。${existingFollows.filter(r => !selectedAccountIds.includes(r.accountId)).length ? '以下账户将停止跟随：' + existingFollows.filter(r => !selectedAccountIds.includes(r.accountId)).map(r => '尾号 ' + r.accountNo.slice(-4)).join('、') : ''}`
          : `影响账户：${existingFollows.filter(r => confirmation === 'pause' ? r.followStatus === 'running' : confirmation === 'stop' || r.id === confirmation).map(r => '尾号 ' + r.accountNo.slice(-4)).join('、')}。暂停、停止或撤销不代表持仓已平仓，账户连接保持。`}
        onCancel={() => setConfirmation(null)} onConfirm={() => void handleConfirmedAction()} />}
      {/* ═══ 底部固定按钮 ═══ */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-5 py-3 z-40"
        style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
        <div className="flex gap-2">
          {isEditMode && hasRunning && (
            <button aria-label="暂停全部跟随" disabled={submitting || loadingData} onClick={() => setConfirmation('pause')}
              className="w-14 h-14 flex-shrink-0 rounded-2xl flex flex-col items-center justify-center border border-amber-200 bg-amber-50 active:scale-95 transition-transform">
              <i className="ri-pause-circle-line text-amber-500 text-xl" />
              <span className="text-[10px] leading-3 text-amber-700">暂停</span>
            </button>
          )}
          {isEditMode && hasActive && (
            <button aria-label="停止全部跟随" disabled={submitting || loadingData} onClick={() => setConfirmation('stop')}
              className="w-14 h-14 flex-shrink-0 rounded-2xl flex flex-col items-center justify-center border border-red-200 bg-red-50 active:scale-95 transition-transform">
              <i className="ri-stop-circle-line text-red-500 text-xl" />
              <span className="text-[10px] leading-3 text-red-600">停止</span>
            </button>
          )}
          <button
            onClick={() => setConfirmation('save')}
            disabled={!canSubmit || submitting}
            className="flex-1 font-bold text-sm py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            style={{
              background: canSubmit && !submitting ? `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)` : '#E5E7EB',
              color: canSubmit && !submitting ? 'white' : '#9CA3AF',
              boxShadow: canSubmit && !submitting ? `0 4px 16px ${agent.color}59` : 'none',
            }}
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                正在保存授权…
              </>
            ) : (
              <>
                <i className="ri-play-circle-line" />
                {selectedAccountIds.length === 0
                  ? '请选择跟单账户'
                  : isEditMode
                  ? `保存并更新（${selectedAccountIds.length} 个账户）`
                  : `确认策略授权（${selectedAccountIds.length} 个账户）`}
              </>
            )}
          </button>
        </div>
        {!canSubmit && selectedAccountIds.length > 0 && (
          <p className="text-center text-gray-400 text-xs mt-2">
            {!riskConfirmed ? '请先勾选风险确认' : config.allowedPairs.length === 0 ? '请至少选择 1 个跟单币种' : ''}
          </p>
        )}
      </div>

      {/* ═══ 账户选择底部弹层（多选）═══ */}
      {showAccountSheet && (
        <div className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowAccountSheet(false)}>
          <div className="bg-white rounded-t-3xl w-full px-5 pt-6 flex flex-col"
            style={{ maxHeight: '75vh', paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px) + 16px)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-900 font-bold text-base">选择交易账户</h3>
              {sheetDraftIds.length > 0 && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: `${agent.color}18`, color: agent.color }}>
                  已选 {sheetDraftIds.length} 个
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2 overflow-y-auto flex-1 pb-4" style={{ maxHeight: '48vh' }}>
              {/* 可选账户 — 简洁卡片 */}
              {accountGroups.connected.length > 0 && (
                <>
                  <p className="text-gray-400 text-[10px] font-medium px-1 mb-1">已接入账户（按当前策略核对）</p>
                  {accountGroups.connected.map((acc) => {
                    const isDraftSelected = sheetDraftIds.includes(acc.id);
                    const compatible = acc.symbolCompatibility?.eligible === true;
                    const selectable = isSelectableAccount(acc);
                    return (
                      <button
                        key={acc.id}
                        onClick={() => toggleDraftAccount(acc.id)}
                        aria-disabled={!selectable && !isDraftSelected}
                        className="flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all active:scale-[0.98] min-h-[56px]"
                        style={{
                          background: isDraftSelected ? `${agent.color}08` : 'white',
                          border: isDraftSelected ? `1.5px solid ${agent.color}40` : '1.5px solid #F0F0F5',
                        }}
                      >
                        {/* 左侧：仅选择控件 */}
                        <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                          style={{
                            background: isDraftSelected ? agent.color : 'white',
                            border: isDraftSelected ? `2px solid ${agent.color}` : '2px solid #D1D5DB',
                          }}>
                          {isDraftSelected && <i className="ri-check-line text-white text-xs" />}
                        </div>
                        {/* 中间：Broker · 尾号 / 平台 + 状态 */}
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-semibold text-sm truncate">
                            {acc.brokerName} <span className="text-gray-400 font-normal">····{acc.accountNo.slice(-4)}</span>
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: `${agent.color}18`, color: agent.color }}>
                              {acc.type.toUpperCase()}
                            </span>
                            <span className="text-gray-400 text-[10px]">{accountStatusConfig[acc.accountStatus]?.label || acc.accountStatus}</span>
                          </div>
                          <p className={`text-[10px] mt-1 ${selectable ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {acc.usageStatus === 'occupied' ? '持仓待处理 · 暂不可选' : compatible ? acc.symbolCompatibility?.message || '品种校验通过' : acc.symbolCompatibility?.message || '品种待验证 · 暂不可选'}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}

              {/* 全选 / 全不选 */}
              {accountGroups.connected.length > 1 && (
                <div className="flex gap-2 mt-1 mb-1">
                  <button
                    onClick={() => setSheetDraftIds(selectableAccounts.map((a) => a.id))}
                    className="flex-1 py-2.5 rounded-xl text-xs font-semibold border active:scale-[0.98] transition-transform"
                    style={{ borderColor: `${agent.color}40`, color: agent.color, background: `${agent.color}06` }}>
                    全选可用（{selectableAccounts.length}）
                  </button>
                  <button
                    onClick={() => setSheetDraftIds([])}
                    className="flex-1 py-2.5 rounded-xl text-xs font-semibold border border-gray-200 text-gray-500 bg-gray-50 active:scale-[0.98] transition-transform">
                    清空选择
                  </button>
                </div>
              )}

              {/* 不可选 — 已跟单其他智能体 */}
              {accountGroups.followingOthers.length > 0 && (
                <>
                  <p className="text-gray-400 text-[10px] font-medium px-1 mt-2 mb-1">已跟单其他智能体</p>
                  {accountGroups.followingOthers.map((acc) => (
                    <div key={acc.id}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-50 opacity-60 min-h-[56px]"
                      style={{ border: '1.5px solid #F0F0F5' }}>
                      <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 border-gray-200 bg-gray-100">
                        <i className="ri-subtract-line text-gray-300 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-400 font-semibold text-sm truncate">
                          {acc.brokerName} <span className="text-gray-300 font-normal">····{acc.accountNo.slice(-4)}</span>
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 bg-gray-200 text-gray-400">
                            {acc.type.toUpperCase()}
                          </span>
                          <span className="text-gray-300 text-[10px]">
                            已被其他智能体占用
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* 不可选 — 审核中 */}
              {accountGroups.reviewing.length > 0 && (
                <>
                  <p className="text-gray-400 text-[10px] font-medium px-1 mt-2 mb-1">审核中</p>
                  {accountGroups.reviewing.map((acc) => (
                    <div key={acc.id}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl bg-amber-50/50 opacity-60 min-h-[56px]"
                      style={{ border: '1.5px solid #FEF3C7' }}>
                      <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 border-amber-200 bg-amber-100">
                        <i className="ri-time-line text-amber-400 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 font-semibold text-sm truncate">
                          {acc.brokerName} <span className="text-gray-400 font-normal">····{acc.accountNo.slice(-4)}</span>
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 bg-amber-200 text-amber-600">
                            {acc.type.toUpperCase()}
                          </span>
                          <span className="text-amber-400 text-[10px]">平台审核中</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* 不可选 — 已释放 */}
              {accountGroups.released.length > 0 && (
                <>
                  <p className="text-gray-400 text-[10px] font-medium px-1 mt-2 mb-1">已释放</p>
                  {accountGroups.released.map((acc) => (
                    <div key={acc.id}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-50 opacity-50 min-h-[56px]"
                      style={{ border: '1.5px solid #F0F0F5' }}>
                      <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 border-gray-200 bg-gray-100">
                        <i className="ri-subtract-line text-gray-300 text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-400 font-semibold text-sm truncate">
                          {acc.brokerName} <span className="text-gray-300 font-normal">····{acc.accountNo.slice(-4)}</span>
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 bg-gray-200 text-gray-400">
                            {acc.type.toUpperCase()}
                          </span>
                          <span className="text-gray-300 text-[10px]">需重新绑定</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {accountGroups.connected.length === 0 && accountGroups.followingOthers.length === 0 && accountGroups.reviewing.length === 0 && accountGroups.released.length === 0 && (
                <div className="py-8 text-center">
                  <i className="ri-link-unlink-m text-gray-300 text-3xl" />
                  <p className="text-gray-400 text-sm mt-2">暂无可用账户</p>
                </div>
              )}
            </div>

            {/* 弹层底部操作 */}
            <div className="flex flex-col gap-2 mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={confirmSheetSelection}
                disabled={sheetDraftIds.length === 0}
                className="w-full py-3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 text-white transition-all disabled:cursor-not-allowed"
                style={{
                  background: sheetDraftIds.length > 0
                    ? `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)`
                    : '#E5E7EB',
                  color: sheetDraftIds.length > 0 ? 'white' : '#9CA3AF',
                  boxShadow: sheetDraftIds.length > 0 ? `0 4px 16px ${agent.color}59` : 'none',
                }}>
                <i className="ri-check-line" />
                {sheetDraftIds.length > 0 ? `确认选择（${sheetDraftIds.length} 个账户）` : '请至少选择 1 个账户'}
              </button>
              <button
                onClick={() => { setShowAccountSheet(false); navigate('/broker'); }}
                className="w-full py-3 rounded-2xl border border-dashed flex items-center justify-center gap-2 text-sm font-medium"
                style={{ borderColor: `${agent.color}50`, color: agent.color, background: `${agent.color}06` }}>
                <i className="ri-add-line" />
                添加交易账户
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Toast ═══ */}
      {toast && (
        <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl flex items-center gap-2 shadow-lg"
          style={{ background: toast.type === 'success' ? '#059669' : '#DC2626' }}>
          <i className={`text-white ${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}`} />
          <span className="text-white text-sm font-medium">{toast.msg}</span>
        </div>
      )}


    </div>
  );
}
